import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { Observable } from 'rxjs';
import {
  BaseService,
  CentrifugoService,
  LeagueApi,
  LeagueBanner,
  LeagueCourt,
  LeagueNews,
  LeagueNewsApi,
  LeagueNewsType,
  LeaguePartner
} from '@mtgame/core';
import { LeagueRootService } from '@core/services/league-root.service';
import { GamesHeadlineService } from '../../games-headline/services/games-headline.service';

const STORE_NEWS_KEY = 'news';
const STORE_MAIN_NEWS = 'main-news';
const STORE_PARTNERS_KEY = 'partners';
const STORE_BANNERS_KEY = 'banners';
const STORE_COURTS_KEY = 'courts';

@Injectable({providedIn: 'root'})
export class LeagueHomeService extends BaseService {
  private leagueId: number;

  get mainNews$(): Observable<LeagueNews[]> {
    return this.store.get(STORE_MAIN_NEWS);
  }

  get newsList$(): Observable<LeagueNews[]> {
    return this.store.get(STORE_NEWS_KEY);
  }

  get partners$(): Observable<LeaguePartner[]> {
    return this.store.get(STORE_PARTNERS_KEY);
  }

  get banners$(): Observable<LeagueBanner[]> {
    return this.store.get(STORE_BANNERS_KEY);
  }

  get courts$(): Observable<LeagueCourt[]> {
    return this.store.get(STORE_COURTS_KEY);
  }

  constructor(
    private leagueApi: LeagueApi,
    private leagueNewsApi: LeagueNewsApi,
    private leagueRootService: LeagueRootService,
    private centrifugoService: CentrifugoService,
    private gamesHeadlineService: GamesHeadlineService,
    @Inject(PLATFORM_ID) private platformId: string
  ) {
    super();
  }

  async initialize(): Promise<any> {
    const league = this.leagueRootService.getLeagueModel();
    this.leagueId = league.id;

    return await Promise.all([
      this.getMainNews(),
      this.getNews(),
      this.getPartners(),
      this.getBanners()
    ]);
  }

  async getMainNews(): Promise<LeagueNews[]> {
    const news = await this.leagueNewsApi.getMainLeagueNewsList(this.leagueId);
    this.store.save(STORE_MAIN_NEWS, news);
    return news;
  }

  async getNews(): Promise<LeagueNews[]> {
    const news = await this.leagueNewsApi.getLeagueNewsList(this.leagueId, 1, 5, LeagueNewsType.text);
    this.store.save(STORE_NEWS_KEY, news.data);
    return news.data;
  }

  async getPartners(): Promise<LeaguePartner[]> {
    const partners = await this.leagueApi.getPartners(this.leagueId);
    this.store.save(STORE_PARTNERS_KEY, partners);
    return partners;
  }

  async getBanners(): Promise<LeagueBanner[]> {
    const banners = await this.leagueApi.getBanners(this.leagueId);
    this.store.save(STORE_BANNERS_KEY, banners);
    return banners;
  }

  async getCourts(): Promise<LeagueCourt[]> {
    const courts = await this.leagueApi.getCourts(this.leagueId);
    this.store.save(STORE_COURTS_KEY, courts);
    return courts;
  }
}
